import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import type { MountedExperiment } from '@learnforge/demo-kit';
import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { factor } from '../arith/factorisation';
import { isPrime } from '../arith/primes';
import { createTree, type TreeNode, type TreeHandle } from '../render/tree';
import { createSvgEl } from '@learnforge/demo-render';
import { createActionBar } from '../action-bar';
import { FACTOR_PRESETS } from '../data/factor-presets';
import type { ArithContext, ArithKey } from '../types';

export interface FactorNode {
  value: number;
  isPrime: boolean;
}

// Recursively splits n into its smallest prime factor p and n/p, until the
// leaves are all primes. e.g. 12 → 12[2, 6[2, 3]] with leaves 2, 2, 3.
export function buildFactorTree(n: number): TreeNode<FactorNode> {
  if (n < 2) return { data: { value: n, isPrime: false }, children: [] };
  if (isPrime(n)) return { data: { value: n, isPrime: true }, children: [] };
  const first = factor(n)[0];
  if (first === undefined) return { data: { value: n, isPrime: true }, children: [] };
  const p = first.prime;
  return {
    data: { value: n, isPrime: false },
    children: [{ data: { value: p, isPrime: true }, children: [] }, buildFactorTree(n / p)],
  };
}

const SUPERSCRIPTS = '⁰¹²³⁴⁵⁶⁷⁸⁹';

function toSuperscript(n: number): string {
  return String(n)
    .split('')
    .map((d) => SUPERSCRIPTS[Number(d)])
    .join('');
}

// The factorisation readout shown under the tree, e.g.
//   12 → "12 = 2 × 2 × 3 = 2² × 3"   (expanded product, then primes grouped by power)
//    6 → "6 = 2 × 3"                 (grouped form omitted — every exponent is 1)
//    7 → "7 is prime"                (prime: the tree is a single node)
// `primeText` is injected so the caller can localise the prime phrase.
export function factorFormula(
  n: number,
  primeText: (value: number) => string = (value) => `${value} is prime`,
): string {
  if (n < 2) return String(n);
  if (isPrime(n)) return primeText(n);
  const factors = factor(n);
  const expanded = factors
    .flatMap(({ prime, exponent }) => Array<number>(exponent).fill(prime))
    .join(' × ');
  const grouped = factors
    .map(({ prime, exponent }) =>
      exponent === 1 ? String(prime) : `${prime}${toSuperscript(exponent)}`,
    )
    .join(' × ');
  return expanded === grouped ? `${n} = ${expanded}` : `${n} = ${expanded} = ${grouped}`;
}

const MAX_N = 1_000_000;

const ADVANCED_STORAGE_KEY = 'arithmetic.prime-factor-tree.advanced';

// ── Legacy mount (kept for tests and any callers that import it by name) ──────

interface State {
  n: number;
  advanced: boolean;
}

export function mountPrimeFactorTree(ctx: ArithContext): MountedExperiment {
  const state: State = {
    n: 12,
    advanced: safeStorage.get(ADVANCED_STORAGE_KEY) === '1',
  };

  const svg = createSvgEl('svg', { viewBox: '0 0 800 480', class: 'arith-stage' });
  ctx.root.appendChild(svg);
  const treeGroup = createSvgEl('g', { transform: 'translate(0, 20)' });
  svg.appendChild(treeGroup);
  const formula = createSvgEl('text', {
    class: 'arith-readout',
    x: 400,
    y: 465,
    'text-anchor': 'middle',
  });
  svg.appendChild(formula);

  let tree: TreeHandle<FactorNode> | null = null;
  let manualInput: HTMLInputElement | null = null;

  function presetButtons() {
    return FACTOR_PRESETS.map((p) => ({
      id: `n-${p.value}`,
      labelKey: String(p.value),
      active: p.value === state.n,
      onClick() {
        state.n = p.value;
        rerender();
      },
    }));
  }

  function rerender(): void {
    tree?.destroy();
    tree = createTree(treeGroup, {
      root: buildFactorTree(state.n),
      renderNode: (node) => ({
        label: String(node.data.value),
        className: node.data.isPrime ? 'node-prime' : 'node-composite',
      }),
      width: 800,
      height: 420,
    });
    formula.textContent = factorFormula(
      state.n,
      (value) => `${value} ${ctx.i18n.t('formula.is-prime')}`,
    );
    if (manualInput && manualInput.value !== String(state.n)) {
      manualInput.value = String(state.n);
    }
    actionBar.setButtons(presetButtons());
  }

  const actionBar = createActionBar(ctx.actionBar, presetButtons());
  rerender();

  const advRow = createToggleRow({
    i18n: ctx.i18n,
    labelKey: 'btn.advanced',
    checked: state.advanced,
    onChange(next) {
      state.advanced = next;
      safeStorage.set(ADVANCED_STORAGE_KEY, next ? '1' : '0');
      renderAdvancedControls();
    },
  });
  ctx.panel.appendChild(advRow.element);

  // Advanced mode reveals a free-number input so any value (not just the presets)
  // can be factored.
  const advControls = document.createElement('div');
  advControls.className = 'arith-adv-controls';
  ctx.panel.appendChild(advControls);

  function renderAdvancedControls(): void {
    advControls.replaceChildren();
    manualInput = null;
    if (!state.advanced) return;
    const label = document.createElement('label');
    label.htmlFor = 'prime-factor-tree-n';
    label.textContent = ctx.i18n.t('panel.enter-number');
    const input = document.createElement('input');
    input.id = 'prime-factor-tree-n';
    input.type = 'number';
    input.min = '2';
    input.max = String(MAX_N);
    input.step = '1';
    input.value = String(state.n);
    input.addEventListener('change', () => {
      const v = Math.floor(Number(input.value));
      if (Number.isFinite(v) && v >= 2 && v <= MAX_N) {
        state.n = v;
        rerender();
      }
    });
    advControls.appendChild(label);
    advControls.appendChild(input);
    manualInput = input;
  }

  renderAdvancedControls();

  return {
    dispose() {
      tree?.destroy();
      actionBar.destroy();
      if (svg.parentNode) svg.parentNode.removeChild(svg);
    },
  };
}

// ── Harness spec ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.prime-factor-tree.label',
    demoId: 'arithmetic',
    advancedKey: 'prime-factor-tree',

    initialState(ctx): State {
      return {
        n: 12,
        advanced: ctx.advanced,
      };
    },

    applyPreset(state, presetId): State {
      const preset = FACTOR_PRESETS.find((p) => String(p.value) === presetId || p.id === presetId);
      if (!preset) return state;
      return { ...state, n: preset.value };
    },

    createRender(ctx, host, controls) {
      // ── SVG stage (built once) ────────────────────────────────────────────
      const svg = createSvgEl('svg', { viewBox: '0 0 800 480', class: 'arith-stage' });
      host.appendChild(svg);
      const treeGroup = createSvgEl('g', { transform: 'translate(0, 20)' });
      svg.appendChild(treeGroup);
      const formula = createSvgEl('text', {
        class: 'arith-readout',
        x: 400,
        y: 465,
        'text-anchor': 'middle',
      });
      svg.appendChild(formula);

      // ── Closure state for transient DOM handles ───────────────────────────
      let treeHandle: TreeHandle<FactorNode> | null = null;
      let manualInput: HTMLInputElement | null = null;

      // ── Panel: advanced toggle (built once) ───────────────────────────────
      const advRow = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced',
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_STORAGE_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });
      ctx.panel.appendChild(advRow.element);

      // Advanced-only number input container (built once; shown/hidden via update)
      const advControls = document.createElement('div');
      advControls.className = 'arith-adv-controls';
      ctx.panel.appendChild(advControls);

      // ── Action bar: preset buttons (built once, never rebuilt) ──────────────
      const actionBar = createActionBar(
        ctx.actionBar,
        FACTOR_PRESETS.map((p) => ({
          id: `n-${p.value}`,
          labelKey: String(p.value),
          active: false,
          onClick() {
            controls.setState((s) => ({ ...s, n: p.value }));
          },
        })),
      );

      // ── update: reconcile DOM to state ────────────────────────────────────
      function update(state: State): void {
        // Rebuild tree from state.n (destroy previous handle first)
        treeHandle?.destroy();
        treeHandle = createTree(treeGroup, {
          root: buildFactorTree(state.n),
          renderNode: (node) => ({
            label: String(node.data.value),
            className: node.data.isPrime ? 'node-prime' : 'node-composite',
          }),
          width: 800,
          height: 420,
        });

        // Readout formula
        formula.textContent = factorFormula(
          state.n,
          (value) => `${value} ${ctx.i18n.t('formula.is-prime')}`,
        );

        // Sync number-input value without rebuilding the panel
        if (manualInput && manualInput.value !== String(state.n)) {
          manualInput.value = String(state.n);
        }

        // Sync advanced panel controls (show/hide input)
        if (state.advanced && !manualInput) {
          // Show advanced input
          advControls.replaceChildren();
          const label = document.createElement('label');
          label.htmlFor = 'prime-factor-tree-n';
          label.textContent = ctx.i18n.t('panel.enter-number');
          const input = document.createElement('input');
          input.id = 'prime-factor-tree-n';
          input.type = 'number';
          input.min = '2';
          input.max = String(MAX_N);
          input.step = '1';
          input.value = String(state.n);
          input.addEventListener('change', () => {
            const v = Math.floor(Number(input.value));
            if (Number.isFinite(v) && v >= 2 && v <= MAX_N) {
              controls.setState((s) => ({ ...s, n: v }));
            }
          });
          advControls.appendChild(label);
          advControls.appendChild(input);
          manualInput = input;
        } else if (!state.advanced && manualInput) {
          // Hide advanced input
          advControls.replaceChildren();
          manualInput = null;
        }
      }

      return {
        update,
        dispose() {
          treeHandle?.destroy();
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
